import React from 'react';
import Box from '@mui/material/Box';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { FavoriteSlideIconButton } from 'components/utilities/FavoriteSlide/FavoriteSlideIconButton';
import { SyncViewToggle } from 'components/ImageViewer/syncViewContext';
import { TEMP_GROUP_TITLE } from 'components/ImageViewer/constants';
import { ORDER_DETAILS_URL, ORDERS_SLIDE_LIST_URL } from 'constants/urls';
import { useSlideViewerAppBarStyles } from 'components/ImageViewer/components/SlideViewerAppBar/styles';

const WHITE = '#fff';

// With the breadcrumb and viewer controls in the app bar, only the bar itself
// (64 px) and the main padding sit above the slide, and the viewer box keeps
// just the main/box padding (8 + 8 + 8 px) below the bar instead of 96 px.
export const SLIDE_VIEWER_COMPACT_LAYOUT_VARS = {
  '--slides-list-header-height': '72px',
  '--slide-viewer-vertical-chrome': '24px',
};

/**
 * order / slides / slide-name. `leading` prepends a page-context crumb (a
 * group or project name); `withSlidesCrumb: false` drops the "slides" link on
 * the slides-list pages, where it would only point at the page itself.
 */
export const getSlideBreadcrumbs = (
  slide,
  { leading, withSlidesCrumb = true } = {},
) => [
  ...(leading ? [{ name: leading, color: WHITE }] : []),
  {
    name: slide?.order?.id,
    link: ORDER_DETAILS_URL.replace(':orderUUID', slide?.order?.uuid),
    color: WHITE,
  },
  {
    name: 'slides',
    hide: !withSlidesCrumb,
    link: ORDERS_SLIDE_LIST_URL.replace(':orderUUID', slide?.order?.uuid),
    color: WHITE,
  },
  { name: slide?.name, bold: true, color: WHITE },
];

/**
 * The page-context crumb for a slides-list page: the group or project the
 * list belongs to. An order's list has none, its order id is already the
 * first crumb.
 */
export const getSlidesListLeadingCrumb = ({
  isTempGroup,
  slidesGroupName,
  projectUUID,
  slide,
}) => {
  if (isTempGroup) {
    return TEMP_GROUP_TITLE;
  }

  if (slidesGroupName) {
    return `Group: ${slidesGroupName}`;
  }

  if (projectUUID) {
    return slide?.project?.name;
  }

  return undefined;
};

/**
 * The slide viewer's app-bar title: favourite star plus the
 * order / slides / slide-name breadcrumb that used to sit in a row of its
 * own under the bar. Putting it here gives that row back to the slide.
 */
export const SlideViewerAppBarTitle = ({
  slide,
  leading,
  withSlidesCrumb = true,
  fallback = 'Slide Viewer',
}) => {
  const { classes } = useSlideViewerAppBarStyles();

  if (!slide) {
    return fallback;
  }

  return (
    <Box className={classes.title} data-testid="slide-viewer-app-bar-title">
      <FavoriteSlideIconButton
        slideUUID={slide.uuid}
        className={classes.star}
        filledIconProps={{ className: classes.starIcon }}
        outlineIconProps={{ className: classes.starIcon }}
      />
      <Breadcrumbs
        data={getSlideBreadcrumbs(slide, { leading, withSlidesCrumb })}
        size="medium"
        dividerColor="rgba(255, 255, 255, 0.7)"
      />
    </Box>
  );
};

/**
 * The viewer controls that used to live in the toolbar row under the bar.
 * Viewer Presets moved to the top of the floating annotation rail, and the
 * prev/next buttons are off for now (the arrow keys still change slides).
 */
export const SlideViewerAppBarActions = () => {
  const { classes } = useSlideViewerAppBarStyles();

  return (
    <Box className={classes.actions} data-testid="slide-viewer-app-bar-actions">
      <SyncViewToggle />
    </Box>
  );
};
